import { SIGNATURE_BLOCK_MARKER_ATTR } from './constants';

export function isFragileSignature(html: string): boolean {
  if (!html) {
    return false;
  }
  if (/<table[\s>]/i.test(html)) {
    return true;
  }
  // Plate always injects `style="width: ...; max-width: 100%; height: auto;
  // display: block;"` on its <img> output (see plateEmailSerializer.ts), so
  // an unqualified `style=` check would force any signature containing a
  // logo into the opaque path on its next load. Only a style attribute on a
  // non-<img> element signals genuinely rich inline-styled layout.
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return Array.from(temp.querySelectorAll('[style]')).some(
    el => el.tagName.toLowerCase() !== 'img'
  );
}

// The marker is never guaranteed to be the sole top-level node: Slate/Plate
// normalizes a void-only document by inserting a trailing empty paragraph for
// cursor placement, so after the very first edit the serialized HTML always
// has the marker plus at least one sibling. A single-child check therefore
// stops detecting an already-wrapped signature the moment the user does
// anything, which both breaks unwrap (below) and can lead to double-wrapping.
function isWrapped(html: string): boolean {
  const temp = document.createElement('div');
  temp.innerHTML = html.trim();
  return temp.querySelector(`[${SIGNATURE_BLOCK_MARKER_ATTR}]`) !== null;
}

export function wrapSignatureForEditor(raw: string): string {
  if (!raw || !isFragileSignature(raw)) {
    return raw;
  }
  if (isWrapped(raw)) {
    return raw;
  }
  return `<div ${SIGNATURE_BLOCK_MARKER_ATTR}="true">${raw}</div>`;
}

// See isWrapped above for why a single-child assumption is wrong here too:
// the marker can sit anywhere among top-level siblings (including alongside
// Slate's trailing empty paragraph), and — if it was ever double-wrapped by
// a prior version of this bug — can appear more than once, nested. Every
// matching marker element is located and stripped, wherever it sits, so a
// signature is never left with a residual marker.
export function unwrapSignatureFromEditor(html: string): string {
  if (!html) {
    return html;
  }
  const temp = document.createElement('div');
  temp.innerHTML = html.trim();
  const markers = temp.querySelectorAll(`[${SIGNATURE_BLOCK_MARKER_ATTR}]`);
  if (markers.length === 0) {
    return html;
  }
  markers.forEach(marker => {
    const replacement = document.createDocumentFragment();
    while (marker.firstChild) {
      replacement.appendChild(marker.firstChild);
    }
    marker.replaceWith(replacement);
  });
  return temp.innerHTML;
}
